import { forwardRef, useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { cn } from "@tarawa/ui";
import { useI18n } from "@/lib/i18n";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "md" | "lg" | "sm"; loading?: boolean }
>(({ variant = "primary", size = "md", loading, className, children, disabled, ...props }, ref) => (
  <button
    ref={ref}
    disabled={disabled || loading}
    className={cn(
      "inline-flex items-center justify-center gap-2 rounded-2xl font-semibold transition-[background,transform,box-shadow] duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
      size === "lg" && "h-14 px-6 text-base",
      size === "md" && "h-12 px-5 text-[15px]",
      size === "sm" && "h-9 rounded-xl px-3 text-sm",
      variant === "primary" && "bg-brand-600 text-white shadow-sm hover:bg-brand-700",
      variant === "secondary" && "bg-brand-50 text-brand-800 hover:bg-brand-100",
      variant === "outline" && "border border-line bg-surface text-ink hover:bg-canvas",
      variant === "ghost" && "text-ink hover:bg-black/5",
      variant === "danger" && "bg-terracotta-50 text-terracotta-600 hover:bg-terracotta-100",
      className,
    )}
    {...props}
  >
    {loading && <Loader2 className="size-4 animate-spin" />}
    {children}
  </button>
));
Button.displayName = "Button";

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-6 animate-spin text-brand-600", className)} />;
}

export function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner />
    </div>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  optional?: boolean;
  children: (id: string) => ReactNode;
}

export function Field({ label, error, hint, optional, children }: FieldProps) {
  const id = useId();
  const { t } = useI18n();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
        {optional && <span className="ms-1 font-normal text-subtle">({t("common.optional")})</span>}
      </label>
      {children(id)}
      {error ? (
        <p className="text-sm font-medium text-terracotta-600" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export const inputClass =
  "block h-12 w-full rounded-2xl border border-line bg-surface px-4 text-[16px] text-ink placeholder:text-subtle transition focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 focus:outline-none aria-[invalid=true]:border-terracotta-500";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <input ref={ref} aria-invalid={invalid || undefined} className={cn(inputClass, className)} {...props} />
  ),
);
Input.displayName = "Input";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea ref={ref} className={cn(inputClass, "h-auto min-h-20 py-3", className)} {...props} />
  ),
);
Textarea.displayName = "Textarea";

export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => {
    const [show, setShow] = useState(false);
    const { t } = useI18n();
    return (
      <div className="relative">
        <input
          ref={ref}
          type={show ? "text" : "password"}
          aria-invalid={invalid || undefined}
          className={cn(inputClass, "pe-12", className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-muted hover:text-ink"
          aria-label={show ? t("auth.hidePassword") : t("auth.showPassword")}
        >
          {show ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)]", className)}>{children}</div>;
}

export function PageHeader({ title, back, action }: { title: string; back?: string | true; action?: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <div className="mb-4 flex items-center gap-2">
      {back && (
        <button
          onClick={() => (back === true ? navigate(-1) : navigate(back))}
          className="-ms-2 flex size-10 items-center justify-center rounded-full text-ink hover:bg-black/5"
          aria-label={t("common.back")}
        >
          <ArrowLeft className="size-5 rtl:rotate-180" />
        </button>
      )}
      <h1 className="flex-1 text-xl font-bold tracking-tight md:text-2xl">{title}</h1>
      {action}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: ReactNode;
  title: string;
  text?: string;
  action?: { label: string; to: string };
}) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <div className="mb-4 flex size-20 items-center justify-center rounded-full bg-brand-50 text-brand-600">{icon}</div>
      <h2 className="text-lg font-bold">{title}</h2>
      {text && <p className="mt-1 max-w-xs text-sm text-muted">{text}</p>}
      {action && (
        <Link
          to={action.to}
          className="mt-6 inline-flex h-12 items-center rounded-2xl bg-brand-600 px-6 font-semibold text-white hover:bg-brand-700"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center py-14 text-center">
      <p className="text-muted">{t("common.loadError")}</p>
      {onRetry && (
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      )}
    </div>
  );
}

export function Alert({ kind = "warning", children }: { kind?: "warning" | "error" | "info"; children: ReactNode }) {
  return (
    <div
      role={kind === "error" ? "alert" : undefined}
      className={cn(
        "rounded-2xl px-4 py-3 text-sm font-medium",
        kind === "warning" && "bg-saffron-50 text-saffron-600",
        kind === "error" && "bg-terracotta-50 text-terracotta-600",
        kind === "info" && "bg-brand-50 text-brand-800",
      )}
    >
      {children}
    </div>
  );
}
